import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/sharp-light-svg-icons";
import * as d3 from "d3";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { SensorData } from "../../../../../../apiClient/generated";
import {
    useResizeObserver,
    useZoomOnPlot,
} from "../../../../../../ui/Plot/hooks";
import { selectionContextAtom } from "../../../state";
import { TIMELINE_MARGINS } from "../plot";
import { useSensirionData } from "./hooks";

const Loading = () => (
    <div className="absolute w-full h-full bg-neutral-400 bg-opacity-35 flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
    </div>
);

interface PlotProps {
    data: SensorData[];
    plotType: "methane" | "windSpeed";
    yLabel: string;
    dateFrom: Date;
    dateTo: Date;
    setHoverContext?: (data?: {
        x: Date;
        y: number;
        windDirection?: number;
    }) => void;
}

type PlotDataPoint = {
    date: Date;
    value: number;
    windDirection: number;
};

const SensorDataPlot = ({
    data,
    plotType,
    yLabel,
    dateFrom,
    dateTo,
    setHoverContext,
}: PlotProps) => {
    const svgRef = useRef();
    const containerRef = useRef<HTMLDivElement>();
    const dimensions = useResizeObserver(containerRef);

    // Selection context for highlight indicator
    const [selectionContext] = useAtom(selectionContextAtom);

    // Zoom controls
    const { zoomDomain, addZoomBrushHandle, resetZoom, currentTransform } =
        useZoomOnPlot({
            dimensions,
            svgRef,
            disableDoubleClickReset: true,
        });

    // Transform data based on plot type
    const datasets = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Determine the visible time range
        const visibleStart = zoomDomain.x ? zoomDomain.x[0] : dateFrom;
        const visibleEnd = zoomDomain.x ? zoomDomain.x[1] : dateTo;

        return data
            .map((sensor) => {
                // Filter valid values first
                const allValues = sensor.measurements
                    .map((m) => ({
                        date: new Date(m.timestamp),
                        value:
                            plotType === "methane"
                                ? m.methaneConcentration
                                : m.windSpeed,
                        windDirection:
                            plotType === "windSpeed"
                                ? m.windDirection
                                : undefined,
                    }))
                    .filter(
                        (v) =>
                            v.value !== undefined && Number.isFinite(v.value),
                    );

                // Filter to visible range
                const visibleValues = allValues.filter(
                    (v) => v.date >= visibleStart && v.date <= visibleEnd,
                );

                // Downsample only for wind speed plot
                let sampledValues: PlotDataPoint[];
                if (plotType === "windSpeed") {
                    const targetPoints = 100;
                    if (visibleValues.length <= targetPoints) {
                        // If zoomed in enough, show all available data
                        sampledValues = visibleValues;
                    } else {
                        // Downsample to keep constant 100 points
                        const step = visibleValues.length / targetPoints;
                        sampledValues = [];
                        for (let i = 0; i < targetPoints; i++) {
                            const index = Math.floor(i * step);
                            sampledValues.push(visibleValues[index]);
                        }
                    }
                } else {
                    // No downsampling for methane plot
                    sampledValues = visibleValues;
                }

                return {
                    name: sensor.deviceId,
                    values: sampledValues,
                };
            })
            .filter((d) => d.values.length > 0);
    }, [data, plotType, zoomDomain, dateFrom, dateTo]);

    useEffect(() => {
        const margin = {
            ...TIMELINE_MARGINS,
            bottom: 12,
            top: 12,
        };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        if (!data || data.length === 0) {
            return;
        }

        if (datasets.length === 0) {
            // No valid data to plot
            return;
        }

        chart
            .append("rect")
            .attr("width", width)
            .attr("height", height)
            .attr("fill", "transparent");

        // Clip lines at chart boundaries
        const clipId = `sensirion-clip-${Math.random().toString(36).substr(2, 9)}`;
        const defs = svg.append("defs");

        defs.append("clipPath")
            .attr("id", clipId)
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", width)
            .attr("height", height);

        // Add arrow marker for wind direction
        defs.append("marker")
            .attr("id", "wind-arrow")
            .attr("viewBox", "0 0 10 10")
            .attr("refX", 5)
            .attr("refY", 5)
            .attr("markerWidth", 6)
            .attr("markerHeight", 6)
            .attr("orient", "auto")
            .append("path")
            .attr("d", "M 0 0 L 10 5 L 0 10 L 3 5 z")
            .attr("fill", "#666");

        // Stable color scale based on device IDs, built from the full sensor list
        // (not the filtered datasets) so colors don't shift when a sensor has no
        // data in the visible range.
        const deviceIds = data
            .map((sensor) => sensor.deviceId)
            .sort((a, b) => String(a).localeCompare(String(b)));
        const colorScale = d3
            .scaleOrdinal<string>()
            .domain(deviceIds)
            .range(d3.schemeCategory10);

        // X-axis: UTC time scale
        const xScale = d3
            .scaleUtc()
            .domain(zoomDomain.x || [dateFrom, dateTo])
            .range([0, width]);

        // Y-axis: linear scale
        const allValues = datasets.flatMap((d) => d.values.map((v) => v.value));
        const yMax = d3.max(allValues) || 1;

        const yScale = d3
            .scaleLinear()
            .domain(zoomDomain.y || [0, yMax])
            .nice()
            .range([height - margin.bottom, margin.top]);

        // X-axis grid
        chart
            .append("g")
            .attr("class", "x-grid")
            .attr("transform", `translate(0, ${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .ticks(8)
                    .tickSize(-(height - margin.bottom - margin.top))
                    .tickFormat(() => ""),
            )
            .call((g) => g.select(".domain").remove())
            .call((g) =>
                g
                    .selectAll("line")
                    .attr("stroke-opacity", 0.15)
                    .attr("stroke-dasharray", "3,3")
                    .attr("shape-rendering", "crispEdges"),
            );

        // Y-axis grid
        chart
            .append("g")
            .attr("class", "y-grid")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickSize(-width)
                    .tickFormat(() => ""),
            )
            .call((g) => g.select(".domain").remove())
            .call((g) =>
                g
                    .selectAll("line")
                    .attr("stroke-opacity", 0.15)
                    .attr("stroke-dasharray", "3,3")
                    .attr("shape-rendering", "crispEdges"),
            );

        // Line generator
        const line = d3
            .line<PlotDataPoint>()
            .defined((d) => Number.isFinite(d.value))
            .x((d) => xScale(d.date))
            .y((d) => yScale(d.value))
            .curve(d3.curveCatmullRom.alpha(0.5));

        // Draw lines
        chart
            .append("g")
            .attr("clip-path", `url(#${clipId})`)
            .selectAll(".series-line")
            .data(datasets)
            .join("path")
            .attr("class", "series-line")
            .attr("fill", "none")
            .attr("stroke", (d) => colorScale(d.name))
            .attr("stroke-width", 1)
            .attr("d", (d) => line(d.values) ?? "");

        // Draw wind direction arrows (only for wind speed plot)
        if (plotType === "windSpeed") {
            const arrowGroup = chart
                .append("g")
                .attr("clip-path", `url(#${clipId})`)
                .attr("class", "wind-arrows");

            datasets.forEach((dataset) => {
                dataset.values.forEach((point) => {
                    if (
                        point.windDirection != null &&
                        Number.isFinite(point.windDirection)
                    ) {
                        const x = xScale(point.date);
                        const y = yScale(point.value);

                        // Draw arrow pointing in wind direction
                        // Wind direction: 0° = North (up), 90° = East, 180° = South, 270° = West
                        // Arrow shape: triangular head with notched base for better direction visibility
                        const arrowPath = "M 0,-10 L 6,4 L 0,0 L -6,4 Z";

                        arrowGroup
                            .append("path")
                            .attr("d", arrowPath)
                            .attr(
                                "transform",
                                `translate(${x}, ${y}) rotate(${point.windDirection})`,
                            )
                            .attr("fill", "#444")
                            .attr("stroke", "white")
                            .attr("stroke-width", 0.8)
                            .attr("opacity", 0.85);
                    }
                });
            });
        }

        chart
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${height - margin.bottom})`)
            .call(d3.axisBottom(xScale).ticks(8))
            .append("text")
            .attr("x", width)
            .attr("y", -6)
            .attr("fill", "currentColor")
            .attr("text-anchor", "end")
            .attr("font-size", "10px")
            .text("(UTC)");

        // Y-axis
        chart
            .append("g")
            .attr("class", "y-axis")
            .call(d3.axisLeft(yScale).ticks(5));

        // Y-axis label
        svg.append("text")
            .attr("class", "y-axis-label")
            .attr("text-anchor", "middle")
            .attr("transform", "rotate(-90)")
            .attr("x", -(margin.top + height / 2))
            .attr("y", 15)
            .attr("font-size", "12px")
            .text(yLabel);

        // Legend
        const legend = chart
            .append("g")
            .attr("class", "legend")
            .attr("transform", `translate(5, -5)`)
            .attr("font-family", "sans-serif")
            .attr("font-size", 12);

        const legendItem = legend
            .selectAll("g.legend-item")
            .data(datasets)
            .join("g")
            .attr("class", "legend-item")
            .attr("transform", (_, i) => `translate(${i * 150}, 0)`);

        legendItem
            .append("line")
            .attr("x1", 0)
            .attr("x2", 20)
            .attr("y1", 6)
            .attr("y2", 6)
            .attr("stroke", (d) => colorScale(d.name))
            .attr("stroke-width", 3);

        legendItem
            .append("text")
            .attr("x", 25)
            .attr("y", 10)
            .text((d) => d.name);

        // Add wind direction explanation to legend (only for wind plot)
        if (plotType === "windSpeed") {
            const windLegendX = datasets.length * 150;
            const windLegendGroup = legend
                .append("g")
                .attr("transform", `translate(${windLegendX}, 0)`);
            const arrowPath = "M 0,-10 L 6,4 L 0,0 L -6,4 Z";
            windLegendGroup
                .append("path")
                .attr("d", arrowPath)
                .attr("transform", `translate(15, 9)`)
                .attr("fill", "#444")
                .attr("stroke", "white")
                .attr("stroke-width", 0.8)
                .attr("opacity", 0.85);
            windLegendGroup
                .append("text")
                .attr("x", 25)
                .attr("y", 10)
                .attr("font-size", 10)
                .attr("fill", "black")
                .text("Wind direction (up is North)");
        }

        // Show highlight line if emission is selected
        if (selectionContext?.highlightPosition) {
            chart
                .append("line")
                .attr("class", "highlight-line")
                .attr("x1", xScale(selectionContext.highlightPosition.date))
                .attr("x2", xScale(selectionContext.highlightPosition.date))
                .attr("y1", 0)
                .attr("y2", height)
                .attr("stroke", "#3b82f6")
                .attr("stroke-width", 2)
                .style("stroke-dasharray", "4,4")
                .style("pointer-events", "none");
        }

        // Integrate zoom
        addZoomBrushHandle({
            chart,
            height,
            width,
            xScale,
            yScale,
            constrainYToZero: true,
        });

        // Hover events for cursor position
        if (setHoverContext) {
            const containerRect = containerRef.current.getBoundingClientRect();

            chart
                .on("mousemove", (event) => {
                    const x = xScale.invert(
                        event.clientX - containerRect.left - margin.left,
                    );
                    const y = yScale.invert(
                        event.clientY - containerRect.top - margin.top,
                    );

                    // Find nearest data point for wind direction
                    let windDirection: number | undefined;
                    if (plotType === "windSpeed" && datasets.length > 0) {
                        // Find the closest point across all datasets
                        let minDistance = Infinity;
                        datasets.forEach((dataset) => {
                            dataset.values.forEach((point) => {
                                const distance = Math.abs(
                                    point.date.getTime() - x.getTime(),
                                );
                                if (distance < minDistance) {
                                    minDistance = distance;
                                    windDirection = point.windDirection;
                                }
                            });
                        });
                    }

                    setHoverContext({ x, y, windDirection });
                })
                .on("mouseout", () => {
                    setHoverContext(undefined);
                });
        }
    }, [
        dimensions,
        data,
        plotType,
        dateFrom,
        dateTo,
        yLabel,
        zoomDomain,
        addZoomBrushHandle,
        datasets,
        setHoverContext,
        selectionContext,
    ]);

    return (
        <div ref={containerRef} className="w-full h-full relative">
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />
            {/* Reset zoom button - only show when zoomed or date range selected */}
            {(currentTransform.k !== 1 ||
                currentTransform.x !== 0 ||
                currentTransform.y !== 0 ||
                zoomDomain.x !== null ||
                zoomDomain.y !== null) && (
                <button
                    type="button"
                    onClick={() => {
                        resetZoom();
                    }}
                    className="absolute top-2 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                    title="Reset zoom"
                >
                    Reset Zoom
                </button>
            )}
        </div>
    );
};

interface PlotContainerProps {
    panel: ReactNode;
    children: ReactNode;
}

const PlotContainer = (props: PlotContainerProps) => (
    <div className="flex min-h-64">
        <div className="p-1 h-full w-[200px]">{props.panel}</div>
        <div className="flex-1 relative">{props.children}</div>
    </div>
);

interface MethaneConcentrationPlotProps {
    sensorData: {
        isLoading: boolean;
        data?: SensorData[];
    };
    dateFrom: DateTime;
    dateTo: DateTime;
}

const MethaneConcentrationPlot = (props: MethaneConcentrationPlotProps) => {
    const [methaneHoverContext, setMethaneHoverContext] = useState<{
        x: Date;
        y: number;
    }>();

    return (
        <PlotContainer
            panel={
                <>
                    <p className="text-neutral-500">
                        CH<sub>4</sub> - Methane Concentration
                    </p>
                    <p>
                        Displaying methane concentration data from{" "}
                        {props.dateFrom.toFormat("MMM dd, yyyy, HH:mm")} to{" "}
                        {props.dateTo.toFormat("MMM dd, yyyy, HH:mm")}
                    </p>
                    {methaneHoverContext && (
                        <div className="mt-2 text-xs text-ae-slate-600">
                            Cursor at:
                            <br />
                            Date:{" "}
                            {DateTime.fromJSDate(methaneHoverContext.x)
                                .toUTC()
                                .toFormat("MMM dd, yyyy, h:mm:ss a")}{" "}
                            UTC
                            <br />
                            CH<sub>4</sub>: {methaneHoverContext.y.toFixed(2)}{" "}
                            ppm
                        </div>
                    )}
                </>
            }
        >
            {props.sensorData.isLoading && <Loading />}
            {props.sensorData.data && (
                <SensorDataPlot
                    data={props.sensorData.data}
                    plotType="methane"
                    yLabel="Methane Concentration (ppm)"
                    dateFrom={new Date(props.dateFrom.toISO())}
                    dateTo={new Date(props.dateTo.toISO())}
                    setHoverContext={setMethaneHoverContext}
                />
            )}
        </PlotContainer>
    );
};

interface WindSpeedPlotProps {
    sensorData: {
        isLoading: boolean;
        data?: SensorData[];
    };
    dateFrom: DateTime;
    dateTo: DateTime;
}

const WindSpeedPlot = (props: WindSpeedPlotProps) => {
    const [windHoverContext, setWindHoverContext] = useState<{
        x: Date;
        y: number;
        windDirection?: number;
    }>();

    return (
        <PlotContainer
            panel={
                <>
                    <p className="text-neutral-500">Wind speed and direction</p>
                    <p>
                        Showing wind speed and direction data from{" "}
                        {props.dateFrom.toFormat("MMM dd, yyyy, HH:mm")} to{" "}
                        {props.dateTo.toFormat("MMM dd, yyyy, HH:mm")}
                    </p>
                    {windHoverContext && (
                        <div className="mt-2 text-xs text-ae-slate-600">
                            Cursor at:
                            <br />
                            Date:{" "}
                            {DateTime.fromJSDate(windHoverContext.x)
                                .toUTC()
                                .toFormat("MMM dd, yyyy, h:mm:ss a")}{" "}
                            UTC
                            <br />
                            Wind Speed: {windHoverContext.y.toFixed(2)} m/s
                            {windHoverContext.windDirection != null && (
                                <>
                                    <br />
                                    Wind Direction:{" "}
                                    {windHoverContext.windDirection.toFixed(0)}°
                                </>
                            )}
                        </div>
                    )}
                </>
            }
        >
            {props.sensorData.isLoading && <Loading />}
            {props.sensorData.data && (
                <SensorDataPlot
                    data={props.sensorData.data}
                    plotType="windSpeed"
                    yLabel="Wind Speed (m/s)"
                    dateFrom={new Date(props.dateFrom.toISO())}
                    dateTo={new Date(props.dateTo.toISO())}
                    setHoverContext={setWindHoverContext}
                />
            )}
        </PlotContainer>
    );
};

interface SensirionDetailPlotsProps {
    siteId: string;
    dateFrom: DateTime;
    dateTo: DateTime;
}

export const SensirionDetailPlots = (props: SensirionDetailPlotsProps) => {
    const sensorData = useSensirionData({
        siteId: props.siteId,
        startDate: new Date(props.dateFrom.toISO()),
        endDate: new Date(props.dateTo.toISO()),
        dataType: "sensor",
    });

    return (
        <>
            <hr />
            <WindSpeedPlot
                sensorData={sensorData}
                dateFrom={props.dateFrom}
                dateTo={props.dateTo}
            />
            <hr />
            <MethaneConcentrationPlot
                sensorData={sensorData}
                dateFrom={props.dateFrom}
                dateTo={props.dateTo}
            />
        </>
    );
};
